import React, { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { motion } from "framer-motion";
import { Trophy, Star, Users, Bot, RefreshCw } from "lucide-react";
import { EnhancedHeader } from "@/components/ui";
import { Capacitor } from "@capacitor/core";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface Achievement {
  id: number;
  name: string;
  description: string;
  category: string;
  requirement: number;
  imageUrl: string;
  progress: number;
  completed: boolean;
  completedAt: string | null;
  rewardAmount: number;
  rewardType: string;
  isActive: boolean;
}

export default function Achievements() {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Use React Query to fetch and cache achievements
  const { data: achievements = [], isLoading } = useQuery({
    queryKey: ["achievements", user?.id],
    queryFn: async () => {
      try {
        let apiUrl = `/api/achievements/user/${user?.id}`;
        if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
          apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
        }
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch achievements");
        }
        return await response.json();
      } catch (error) {
        console.error("Failed to fetch achievements:", error);
        throw error;
      }
    },
    enabled: !!user,
  });

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["achievements", user?.id] });
    return Promise.resolve();
  };

  const categories = [
    { id: "all", name: "All", icon: Star },
    { id: "game_milestones", name: "Milestones", icon: Trophy },
    { id: "skill_based", name: "Skills", icon: Star },
    { id: "social", name: "Social", icon: Users },
    { id: "ai_challenge", name: "AI", icon: Bot },
  ];

  const filteredAchievements =
    selectedCategory === "all"
      ? achievements
      : achievements.filter((a) => a.category === selectedCategory);

  const totalProgress = achievements.reduce(
    (acc, curr) => acc + (curr.completed ? 1 : 0),
    0,
  );

  const progressPercentage = achievements.length > 0 
    ? (totalProgress / achievements.length) * 100 
    : 0;

  return (
    <div>
      <EnhancedHeader
        title="Achievements"
        backPath="/profile"
        coloredText={true}
        rightContent={null}
        showBackButton={true}
      />
      <PullToRefresh 
        onRefresh={handleRefresh} 
        pullingContent={
          <div className="text-center p-2">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
          </div>
        }
        refreshingContent={
          <div className="text-center p-4">
            <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="text-sm text-white/60 mt-1">Refreshing...</p>
          </div>
        }
      >
        <div className="min-h-screen bg-[#0A192F] container mx-auto px-4 py-8 pb-20">
          <div className="mb-8">
            <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-300">Overall Progress</span>
                <span className="text-sm text-primary">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 1 }}
                  className="h-full bg-primary"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-start gap-2 mb-8 overflow-x-auto px-4 py-2 w-full max-w-full scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {categories.map(({ id, name, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSelectedCategory(id)}
                className={`flex items-center gap-2 px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap
                  ${
                    selectedCategory === id
                      ? "bg-primary text-black"
                      : "bg-black/30 text-gray-200 hover:bg-black/40 border border-white/10"
                  }`}
              >
                <Icon
                  className={`w-4 h-4 ${selectedCategory === id ? "text-yellow-400" : ""}`}
                />
                {name}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="h-10 w-10 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAchievements.map((achievement) => (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`relative p-4 rounded-lg border transition-all
                    ${
                      achievement.completed
                        ? "bg-black/40 border-emerald-500/50"
                        : "bg-black/20 border-white/10"
                    }`}
                >
                  {achievement.completed && (
                    <div className="absolute top-2 right-2">
                      <Trophy className="w-5 h-5 text-primary" />
                    </div>
                  )}
                  <div className="flex gap-6 mb-3">
                    <div className="w-20 h-20 rounded-lg bg-black/50 p-2 flex-shrink-0">
                      <img
                        src={achievement.imageUrl}
                        alt={achievement.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white text-lg mb-1">
                        {achievement.name}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {achievement.description}
                      </p>
                      <p className="flex justify-between text-sm">
                        <span className="text-gray-400">Progress</span>
                        <span className="text-primary">
                          {achievement.progress} / {achievement.requirement}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{
                          width: `${(achievement.progress / achievement.requirement) * 100}%`,
                        }}
                        transition={{ duration: 0.5 }}
                        className={`h-full rounded-full ${
                          achievement.completed
                            ? "bg-emerald-500"
                            : "bg-yellow-500/70"
                        }`}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </PullToRefresh>
    </div>
  );
}
